import { Change } from '../../../../src/apps/selection/patch-values.mjs';
import { isAbsenceValue } from '../../../../src/apps/selection/slot-presentation.mjs';

const unavailable = Object.freeze({ status: 'unavailable' });
const properties = ['text', 'colour', 'spacing', 'numberFormat'];

// INS supplies current targets/details; the admitted host supplies copy and
// governed binding/crop facts. Display labels never become target authority.
export function projectInspectTarget(target, order, { view, labels, describeTarget }) {
  const facts = describeTarget(target);
  const covered = view?.targetIdFor(target.pid) === target.targetId;
  const current = covered && target.status === 'current';
  const details = current && target.details?.targetId === target.targetId ? target.details : null;
  const slots = details?.slots ?? [];
  const source = details?.source.status === 'available' ? details.source.value : null;
  return Object.freeze({
    targetId: target.targetId,
    nodeId: target.pid,
    order,
    label: covered ? view.label(target.pid) : labels.unavailable,
    context: facts.context ?? null,
    status: current ? 'current' : 'stale',
    editable: properties.filter((kind) =>
      slots.some((slot) => slot.property === kind && slot.editable),
    ),
    scopeLabel: facts.scopeLabel ?? null,
    where: facts.where ?? null,
    shows: facts.shows ?? null,
    changesHere: facts.changesHere ?? null,
    // Exact INS metadata feeds DES2's typed controls, including absence and seed.
    details,
    slots: slots.map((slot) => ({
      slotId: slot.slotId,
      kind: slot.property,
      name: labels.slot(slot),
      ...(slot.presentation?.kind === 'numberFormat'
        ? { valueType: slot.presentation.valueType }
        : {}),
      current: { status: 'ready', value: labels.value(slot.value, slot) },
    })),
    source:
      details?.source.status === 'redacted'
        ? { status: 'redacted' }
        : source?.kind === 'code'
          ? { status: 'ready', value: { file: source.file, line: source.line } }
          : unavailable,
    binding: current ? (facts.binding ?? unavailable) : unavailable,
    crop: current ? (facts.crop ?? unavailable) : unavailable,
  });
}

export function sameInspectValue(left, right) {
  if (left === right) return true;
  if (!left || !right || typeof left !== 'object' || typeof right !== 'object') return false;
  const keys = Object.keys(left).sort();
  const other = Object.keys(right).sort();
  return (
    keys.length === other.length &&
    keys.every((key, index) => key === other[index] && left[key] === right[key])
  );
}

// Convert only already-declared slots. Never infer paths, defaults, currency,
// presence or shared scope from visible text or the requested value.
export function bindInspectChanges(snapshot, input) {
  if (
    snapshot.state !== 'on' ||
    !snapshot.targets.length ||
    snapshot.targets.some((t) => t.status !== 'current')
  )
    throw new TypeError('selection_not_current');
  if (!Array.isArray(input) || input.length < 1 || input.length > 128)
    throw new TypeError('unsupported_target_slot');
  const changes = input.map((change) => Change.parse(change));
  const declared = new Map();
  for (const target of snapshot.targets) {
    if (target.details?.targetId !== target.targetId) throw new TypeError('selection_unavailable');
    for (const slot of target.details.slots) {
      if (declared.has(slot.slotId)) throw new TypeError('ambiguous_target_slot');
      declared.set(slot.slotId, { slot, targetId: target.targetId });
    }
  }
  const touched = new Set();
  const seen = new Set();
  for (const change of changes) {
    const binding = declared.get(change.slotId);
    if (!binding || binding.slot.property !== change.property || seen.has(change.slotId))
      throw new TypeError('unsupported_target_slot');
    const { slot, targetId } = binding;
    if (!sameInspectValue(slot.value, change.oldValue)) throw new TypeError('old_value_changed');
    if ('action' in change ? !slot.clearable : !slot.editable || isAbsenceValue(change.newValue))
      throw new TypeError('unsupported_target_slot');
    seen.add(change.slotId);
    touched.add(targetId);
  }
  if (snapshot.targets.some((target) => !touched.has(target.targetId)))
    throw new TypeError('unsupported_target_slot');
  return changes;
}

export function changesForInspectProperty(snapshot, property, value) {
  if (!properties.includes(property)) throw new TypeError('unsupported_target_slot');
  return snapshot.targets.map((target) => {
    const slots =
      target.details?.slots.filter((slot) => slot.property === property && slot.editable) ?? [];
    if (slots.length !== 1) throw new TypeError('ambiguous_target_slot');
    return { slotId: slots[0].slotId, property, oldValue: slots[0].value, newValue: value };
  });
}
